import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import VideoCard from "@/components/base/VideoCard";
import FilterPanel from "@/components/feature/FilterPanel";
import SortBar from "@/components/feature/SortBar";
import { categories } from "@/mocks/categories";
import { videos } from "@/mocks/videos";
import { applyFilters, defaultFilters, type FilterState } from "@/utils/filter";

const PAGE_SIZE = 6;
const allSubs = categories.flatMap((c) => c.subs);

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [input, setInput] = useState(query);
  const [filters, setFilters] = useState<FilterState>({ ...defaultFilters });
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useEffect(() => {
    setInput(query);
    setVisible(PAGE_SIZE);
  }, [query]);

  const results = useMemo(() => applyFilters(videos, filters, query), [filters, query]);
  const popular = useMemo(() => [...videos].sort((a, b) => b.views - a.views).slice(0, 4), []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setSearchParams(q ? { q } : {});
    setVisible(PAGE_SIZE);
  };

  const updateFilters = (next: FilterState) => {
    setFilters(next);
    setVisible(PAGE_SIZE);
  };

  const reset = () => updateFilters({ ...defaultFilters });
  const shown = results.slice(0, visible);

  return (
    <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
      <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="transition-colors hover:text-primary-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-200">搜索</span>
      </nav>

      <header className="mt-6 rounded-lg border border-background-800 bg-background-900 p-5 md:p-6">
        <h1 className="font-heading text-xl font-extrabold text-foreground-50 md:text-2xl">搜索结果</h1>
        <form onSubmit={submit} className="mt-4 flex flex-col gap-3 sm:flex-row">
          <div className="flex flex-1 items-center gap-2 rounded-md border border-background-700 bg-background-950 px-3 py-2.5 focus-within:border-primary-500">
            <i className="ri-search-line text-base text-foreground-500" />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入关键词，如：MOBA、高音、脱口秀"
              className="w-full bg-transparent text-sm text-foreground-100 outline-none placeholder:text-foreground-600"
            />
            {input && (
              <button
                type="button"
                aria-label="清空"
                onClick={() => setInput("")}
                className="flex h-6 w-6 items-center justify-center rounded-full text-foreground-500 hover:text-foreground-200"
              >
                <i className="ri-close-line" />
              </button>
            )}
          </div>
          <button
            type="submit"
            className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 font-label text-sm font-bold text-background-950 transition-colors hover:bg-primary-400"
          >
            重新搜索
          </button>
        </form>
        <p className="mt-3 text-sm text-foreground-400">
          {query ? (
            <>
              关键词「<span className="font-semibold text-primary-400">{query}</span>」共匹配到{" "}
              <span className="font-label font-bold text-foreground-100">{results.length}</span> 个视频
            </>
          ) : (
            <>还没有输入关键词，以下为全部视频，可使用上方筛选进一步缩小范围。</>
          )}
        </p>
      </header>

      <div className="mt-6 flex flex-col gap-6 lg:flex-row">
        <aside className="hidden w-64 shrink-0 lg:block">
          <div className="sticky top-24 rounded-lg border border-background-800 bg-background-900 p-5">
            <FilterPanel subs={allSubs} filters={filters} onChange={updateFilters} onReset={reset} />
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <div className="mb-4 lg:hidden">
            <button
              type="button"
              onClick={() => setMobileFilterOpen(true)}
              className="flex w-full items-center justify-center gap-2 rounded-md border border-background-700 bg-background-900 px-4 py-2.5 text-sm font-semibold text-foreground-100"
            >
              <i className="ri-filter-3-line" /> 筛选
            </button>
          </div>

          <SortBar sort={filters.sort} onSortChange={(sort) => updateFilters({ ...filters, sort })} total={results.length} />

          {shown.length > 0 ? (
            <>
              <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
                {shown.map((video) => (
                  <VideoCard
                    key={video.id}
                    id={video.id}
                    title={video.title}
                    cover={video.cover}
                    streamerName={video.streamerName}
                    streamerAvatar={video.streamerAvatar}
                    duration={video.duration}
                    views={video.views}
                    subcategory={video.subcategory}
                  />
                ))}
              </div>
              {visible < results.length && (
                <div className="mt-8 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-700 bg-background-900 px-6 py-3 font-label text-sm font-semibold text-foreground-100 transition-colors hover:border-primary-500 hover:text-primary-300"
                  >
                    <i className="ri-add-line text-lg" /> 加载更多视频
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="mt-6 flex flex-col items-center rounded-lg border border-background-800 bg-background-900 px-6 py-14 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-800 text-foreground-500">
                <i className="ri-search-eye-line text-3xl" />
              </span>
              <h3 className="mt-5 font-heading text-lg font-bold text-foreground-50">未找到相关视频</h3>
              <p className="mt-2 max-w-md text-sm text-foreground-400">
                换个关键词试试，或者从下面的分类入口浏览，也可以直接看看热门推荐。
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-2.5">
                {categories.map((cat) => (
                  <Link
                    key={cat.key}
                    to={`/category/${cat.key}`}
                    className="flex items-center gap-1.5 rounded-full border border-background-700 px-4 py-2 text-sm text-foreground-200 transition-colors hover:border-primary-500 hover:text-primary-300"
                  >
                    <i className={`${cat.icon} text-primary-500`} />
                    {cat.name}视频
                  </Link>
                ))}
              </div>
              <button
                type="button"
                onClick={reset}
                className="mt-6 flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 font-label text-sm font-semibold text-background-950 transition-colors hover:bg-primary-400"
              >
                <i className="ri-refresh-line text-lg" /> 重置筛选
              </button>
            </div>
          )}

          {shown.length === 0 && (
            <section className="mt-10">
              <h2 className="mb-5 font-heading text-lg font-bold text-foreground-50">热门视频推荐</h2>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                {popular.map((video) => (
                  <VideoCard
                    key={video.id}
                    id={video.id}
                    title={video.title}
                    cover={video.cover}
                    streamerName={video.streamerName}
                    streamerAvatar={video.streamerAvatar}
                    duration={video.duration}
                    views={video.views}
                    subcategory={video.subcategory}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>

      {mobileFilterOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-background-950/80" onClick={() => setMobileFilterOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto rounded-t-2xl border-t border-background-700 bg-background-900 p-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-base font-bold text-foreground-50">筛选视频</h3>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setMobileFilterOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-background-700 text-foreground-200"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <FilterPanel subs={allSubs} filters={filters} onChange={updateFilters} onReset={reset} />
            <button
              type="button"
              onClick={() => setMobileFilterOpen(false)}
              className="mt-6 w-full rounded-md bg-primary-500 py-3 font-label text-sm font-bold text-background-950"
            >
              查看 {results.length} 个结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}